<template>
  <transition name="user-info">
    <div class="user-info-wrap" v-show="isActiveUserInfo">
      <div class="user-info">
        <div class="user-info__head">
          <button type="button" class="user-info__head-btn" v-if="isMobile" :aria-label="$t('Back')" @click="activeUserInfo">
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"></path></svg>
          </button>
          <h2>{{ $t("Details") }}</h2>
          <button type="button" class="user-info__head-btn is-close" v-if="!isMobile" :aria-label="$t('Close')" @click="activeUserInfo">
            <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
          </button>
        </div>
        <div class="user-info__info">
          <div class="user-info__info-left">
            <a :href="currentUser?.user.public_profile_url" target="_blank" class="user-info__info-img">
              <ChatAvatar :url="currentUser?.user?.avatar || ''" :name="currentUser?.user?.username || ''" :supportName="supportName" :size="64"/>
              <span class="online-status" v-if="currentUser?.user?.username ? currentUser.user.is_online : true"><span class="ico-user-status online"></span></span>
            </a>
            <div class="user-info__info-name">
              <div class="user-name">
                <a :href="currentUser?.user.public_profile_url" target="_blank" :class="{'fade-name': isOverflowName}">{{currentUser.user.username ? currentUser.user.username : supportName}}</a>
                </div>
              <p class="user-region" v-if="currentUser?.user?.country">
                <span class="user-region__flag">
                  <img :src="currentUser.user.country.flag" :alt="currentUser.user.country.code">
                </span>
                <span>{{ formatCountry(currentUser.user.country.name, currentUser.user.timezone_offset) }}</span>
              </p>
            </div>
          </div>
        </div>
        <div class="user-info__tabs" v-if="activeTabs.length > 1">
          <div class="user-info__tabs-wrap">
            <span class="user-info__tabs-indicator" :style="indicatorStyle"></span>
            <span class="user-info__tabs-tab" :class="{active: activeBlock === 'files'}" v-if="!!allChatFiles.length" @click="toggleBlock('files')">
              <svg class="wc-i" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/></svg>
              {{ $t("Files") }} · {{ allChatFiles.length }}
            </span>
            <span class="user-info__tabs-tab" :class="{active: activeBlock === 'images'}" v-if="!!allChatImages.length" @click="toggleBlock('images')">
              <svg class="wc-i" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
              {{ $t("Images") }} · {{ allChatImages.length }}
            </span>
          </div>
        </div>
        <div class="user-info__empty" v-if="!allChatFiles.length && !allChatImages.length">
          <svg class="wc-i" aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
          <p>{{ $t("No shared files or images yet") }}</p>
        </div>
        <div class="user-info__content" ref="modalChatContainer" :class="{mini: miniChat || isMobile}" @touchstart="handleTouchStart($event)" @touchend="handleTouchEnd($event)">
          <div class="content-block files" v-if="activeBlock === 'files'">
            <div class="media-date-group" v-for="group in groupMediaByDate(allChatFiles)" :key="group.date">
                <div class="media-date" @click="handleDateHeaderClick">
                    <div class="media-date__block">
                        {{ group.date }}
                    </div>
                </div>
                <div class="files-list">
                    <div class="file" v-for="file in group.items" :key="file.id">
                        <div class="file__preview-modal" :class="getPreviewClass(file.original_name)"></div>
                        <span>{{ truncateName(file.original_name, 30) }}</span>
                        <div class="file__btn">
                            <span @click="$emit('message-find', file.message_id)">
                                <svg class="wc-i" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>
                            </span>
                            <span @click="$emit('open-file', [file.id, file.original_name, 'download'])"><BaseIcon :name="'message-file-download'" size="20"/></span>
                        </div>
                    </div>
                </div>
            </div>
          </div>
          <div class="content-block images" v-else-if="activeBlock === 'images'">
            <div class="media-date-group" v-for="group in groupMediaByDate(allChatImages)" :key="group.date">
                <div class="media-date" @click="handleDateHeaderClick">
                    <div class="media-date__block">
                        {{ group.date }}
                    </div>
                </div>
                <div class="media-grid">
                    <div class="media-img"
                        v-for="img in group.items"
                        :key="img.id"
                        :style="{backgroundImage: `url(${img.preview_url})`}"
                        @click="$emit('open-img', [img.id, true])"
                        >
                        <img :src="img.preview_url" :alt="img.original_name"/>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
  import ChatAvatar from './ChatAvatar.vue';
  import MuteUser from './MuteUser.vue';
  import ReportUser from './ReportUser.vue';
  import Button from '/webpack-assets/vue/components/Button.vue';
  import BaseIcon from '/webpack-assets/vue/components/BaseIcon.vue';

  export default {
    emits: ['userInfo', 'openFile', 'messageFind', 'openImg', 'userMute', 'isEnableInfo'],
    components: {
      ChatAvatar,
      MuteUser,
      ReportUser,
      Button,
      BaseIcon
    },
    data() {
      return {
        activeBlock: null,
        computedMaxHeight: '0px',
        touchStartX: 0,
        touchStartY: 0,
        timeZoneOffset: '',
        isOverflowName: false,
        supportImg: '/img/icons/logo-small.svg',
        supportName: 'WYREL',
        showMoreType: null,
        activeToolipInfo: false
      };
    },
    props: {
      isActiveUserInfo: {
        type: Boolean,
        default: false
      },
      isLoadingMessages: {
        type: Boolean,
        default: false
      },
      myUser: {
        type: Object,
        required: true
      },
      currentUser: {
        type: Object,
        required: true
      },
      currentUserUuidProp: {
        type: String,
        required: true
      },
      isTablet: {
        type: Boolean,
        default: false
      },
      isMobile: {
        type: Boolean,
        default: false
      },
      miniChat: {
        type: Boolean,
        default: false
      },
      language: {
        type: String,
        default: 'en'
      },
      allChatImages: {
        type: Array,
        default: []
      },
      allChatFiles: {
        type: Array,
        default: []
      },
      activeTooltipBody: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      activeTabs() {
        const tabs = [];

        if ((this.allChatFiles?.length || 0) && !this.miniChat) {
          tabs.push('files');
        }

        if ((this.allChatImages?.length || 0) && !this.miniChat) {
          tabs.push('images');
        }

        if (!tabs.includes(this.activeBlock)) {
          this.activeBlock = tabs.length ? tabs[0] : null;
        }

        return tabs;
      },
      currentTabIndex() {
        return this.activeTabs.indexOf(this.activeBlock);
      },
      indicatorStyle() {
        const count = this.activeTabs.length;
        if (count === 0 || this.currentTabIndex === -1) return {};
        const widthPercent = 100 / count;
        const leftPercent = this.currentTabIndex * widthPercent;
        return {
          width: `${widthPercent}%`,
          left: `${leftPercent}%`
        };
      }
    },
    methods: {
      handleDateHeaderClick(event) {
        const chatContainerEl = this.$refs.modalChatContainer;
        const parent = event.target.closest('.media-date-group');
        
        if (!chatContainerEl || !parent) return;

        const parentPositionTop = parent.offsetTop;

        chatContainerEl.scrollTo({
          top: parentPositionTop,
          behavior: 'smooth',
        });
      },
      formatCountry(name, offset) {
        const num = Number(offset);

        const sign = num >= 0 ? '+' : '-';
        const hours = String(Math.abs(num)).padStart(2, '0');

        return `${name} (UTC ${sign}${hours}:00)`;
      },
      onImgError(event) {
        event.target.src = this.supportImg;
      },
      groupMediaByDate(items) {
          const today = new Date();
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);

          const todayText = this.$t('Today');
          const yesterdayText = this.$t('Yesterday');
          const language = this.language;

          const getDateStart = (date) =>
              new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

          const formatDate = (date) => {
              if (date.toDateString() === today.toDateString()) return todayText;
              if (date.toDateString() === yesterday.toDateString()) return yesterdayText;

              return date.toLocaleDateString(language, {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
              });
          };

          const grouped = {};

          items.forEach(item => {
              if (!item.created_at) return;

              const date = new Date(item.created_at);
              const dateKey = getDateStart(date);

              if (!grouped[dateKey]) {
              grouped[dateKey] = {
                  dateKey,
                  dateLabel: formatDate(date),
                  items: [],
              };
              }

              grouped[dateKey].items.push(item);
          });

          return Object.values(grouped)
              .sort((a, b) => b.dateKey - a.dateKey)
              .map(group => ({
              date: group.dateLabel,
              items: group.items.sort(
                  (a, b) => b.timemark - a.timemark 
              ),
          }));
      },
      activeUserInfo() {
        this.$emit('user-info');
      },
      toggleBlock(blockName) {
        if (this.activeBlock === blockName) return;
        this.activeBlock = blockName;
      },
      handleTouchStart(event) {
        if(this.isTablet) {
          this.touchStartX = event.changedTouches[0].clientX;
          this.touchStartY = event.changedTouches[0].clientY;
        }
      },
      getPreviewClass(name) {
        const ext = (this.getExtension(name) || '').toLowerCase();
        const allowed = ['jpg','jpeg','png','gif','pdf','rar','zip','7z','txt','csv','xls','xlsx'];
        return allowed.includes(ext) ? ext.replace('jpeg','jpg') : 'unknown';
      },
      getExtension(name) {
        return name.split('.').pop().toUpperCase();
      },
      truncateName(name, len = 20) {
        if (name.length <= len) return name;
        const ext = name.split('.').pop();
        return name.substr(0, len - ext.length - 3) + '...' + ext;
      },
      handleTouchEnd(e) {
        if(this.isTablet) {
          const touchEndX = e.changedTouches[0].clientX;
          const touchEndY = e.changedTouches[0].clientY;

          const deltaX = touchEndX - this.touchStartX;
          const deltaY = touchEndY - this.touchStartY;

          if (Math.abs(deltaY) < 20 && deltaX > 50) {
            this.activeUserInfo();
          }
        }
      },
      userMute(id, isMute) {
        if (this.currentUser && id === this.currentUserUuidProp) {
          this.currentUser.is_mute = isMute;
          this.$emit('user-mute', {
            userId: id,
            isMute: isMute
          });
        }
      },
      updateTime() {
        const now = new Date();
        const timezoneOffsetMinutes = -now.getTimezoneOffset();
        const timezoneOffsetHours = Math.floor(timezoneOffsetMinutes / 60);
        const timezoneOffsetFormatted = String(Math.abs(timezoneOffsetHours)).padStart(2, '0') + ':00';

        this.timeZoneOffset = (timezoneOffsetHours >= 0 ? '+' : '-') + timezoneOffsetFormatted;
      },
    }
  };
</script>

<style lang="scss" scoped>
  .user-info-enter-active,
  .user-info-leave-active {
      transition: transform 0.6s ease;
  }
  .user-info-enter-from,
  .user-info-leave-to {
      transform: translate(100%, 0);
  }
  .user-info-enter-to,
  .user-info-leave-from {
      transform: translate(0);
  } 
    .order-info-wrap,
    .user-info-wrap{
      width: 100%;
      background-color: var(--wc-surface);
      border-left: 1px solid var(--wc-border);
      z-index: 6;
      height: 100%;
      overflow: hidden;
      will-change: transform;
      transition: transform 0.6s ease;

      max-width: 320px;
      position: absolute;
      right: 0;
      top: 0;
      @media only screen and (max-width: 1438px) {
        box-shadow: -12px 0 32px var(--wc-shadow-strong);
      }
      @media only screen and (max-width: 767px) {
        min-width: 100%;
        max-width: 100%;
        height: 100%;
      }
    }
    .order-info-wrap{
      z-index: 8;
      .content-block{
        border-radius: 8px;
        overflow: hidden;
        padding: 20px;
        display: block;
        overflow-y: auto;
        max-height: calc(100% - 65px);
      }
      &__order-head{
        background-color: var(--wc-bg);
        box-shadow: 0px 4px 11px 0px var(--wc-shadow);
        display: flex;
        align-items: center;
        padding: 20px;
        gap: 10px;
        &-left{
          display: flex;
          align-items: center;
          gap: 10px;
        }
        &-icon{
          border-radius: 50%;
          border: 0.66px solid var(--wc-accent-border);
          background: var(--wc-accent-soft);
          width: 25px;
          height: 25px;
          min-width: 25px;
          display: flex;
          align-items: center;
          justify-content: center;
          &.close-dispute,
          &.delivered{
              background: var(--wc-success-soft);
              border: 0.66px solid var(--wc-success);
              :deep(.wrap){
                  width: 15px;
                  height: 15px;
                  min-width: 15px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background-color: var(--wc-success);
                  border-radius: 50%;
                  padding-left: 1px;
              }
          }
          &.close-dispute-red{
            background: var(--wc-danger-soft);
            border: 0.66px solid var(--wc-danger-soft);
            :deep(.wrap){
                width: 16px;
                height: 16px;
                min-width: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: var(--wc-danger-soft);
                border-radius: 50%;
            }
          }
        }
        &-title{
          display: flex;
          flex-direction: column;
          h3{
            font-family: "Roboto-Regular";
            font-weight: 400;
            font-size: 16px;
            line-height: 24px;
            letter-spacing: 0px;
            color: var(--wc-text-2);
            white-space: nowrap;
            margin: 0;
          }
        }
        &-progress{
          display: flex;
          align-items: center;
          gap: 5px;
          width: 100%;
          max-width: 565px;
          span{
            width: 100%;
            height: 4px;
            background: var(--wc-surface-2);
            border-radius: 4px;
            background-size: 200% !important;
          }
          @keyframes wave-placeholder {
              from {
                  background-position: 100% 0%;
              }
              to {
                  background-position: -100% 0%;
              }
          }
          &.delivery{
              .progres-1{
                  background: var(--wc-accent-soft);
              }
              .progres-2{
                  background: linear-gradient(90deg, var(--wc-skeleton-1) 0% 25%, var(--wc-skeleton-2) 50%, var(--wc-skeleton-1) 65% 100%);
                  animation: 1s wave-placeholder infinite linear !important;
              }
          }
          &.verification{
              .progres-1{
                  background: linear-gradient(90deg, var(--wc-skeleton-1) 0% 25%, var(--wc-skeleton-2) 50%, var(--wc-skeleton-1) 65% 100%);
                  animation: 1s wave-placeholder infinite linear !important;
              }
          }
          &.delivered{
              .progres-1,
              .progres-2,
              .progres-3{
                  background: var(--wc-success-soft);
              }
          }
          &.close-dispute-red,
          &.close-dispute{
              .progres-1,
              .progres-2,
              .progres-3{
                  background: var(--wc-success-soft);
              }
          }
        }
        &-right{
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          width: 100%;
          &-line{
            width: 1px;
            height: 22px;
            background: var(--wc-bg);
          }
          svg{
            min-width: 24px;
            cursor: pointer;
          }
        }
      }
    }
    .user-info{
      //min-width: 400px;
      display: flex;
      flex-direction: column;
      height: 100%;
      position: relative;
      // @media only screen and (max-width: 1919px) {
      //   min-width: 300px;
      // }
      @media only screen and (max-width: 1023px) {
        padding: 0;
      }
      &__empty{
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 32px 24px;
        text-align: center;
        .wc-i{
          width: 48px;
          height: 48px;
          padding: 12px;
          border-radius: 12px;
          background: var(--wc-bg);
          color: var(--wc-text-3);
          box-sizing: border-box;
        }
        p{
          margin: 0;
          font-family: var(--wc-font);
          font-size: 13px;
          line-height: 18px;
          color: var(--wc-text-2);
        }
      }
      // Header bar (design: "Details" + close / back)
      &__head{
        height: 56px;
        flex: none;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 0 8px 0 16px;
        border-bottom: 1px solid var(--wc-border);
        h2{
          margin: 0;
          font-family: var(--wc-font-heading);
          font-size: 15px;
          line-height: 20px;
          color: var(--wc-text);
        }
        &-btn{
          width: 32px;
          height: 32px;
          border: 0;
          border-radius: 6px;
          background: transparent;
          color: var(--wc-text-2);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          @media only screen and (max-width: 1023px) {
            width: 40px;
            height: 40px;
          }
          &:hover{
            background: var(--wc-bg);
          }
          // not ".close": Bootstrap fades that class to 0.2 opacity
          &.is-close{
            margin-left: auto;
          }
        }
      }
      &__tabs{
        flex: none;
        padding: 12px 16px 0;
        background-color: var(--wc-surface);
        &-wrap{
          height: 32px;
          padding: 2px;
          border-radius: 8px;
          display: flex;
          gap: 2px;
          background-color: var(--wc-bg);
          position: relative;
        }
        &-indicator{
          position: absolute;
          top: 2px;
          left: 0;
          width: 50%;
          height: calc(100% - 4px);
          border-radius: 6px;
          background-color: var(--wc-surface);
          box-shadow: 0 1px 2px rgba(15, 23, 41, 0.08);
          z-index: 2;
          transition: left 0.4s ease;
        }
        &-tab{
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
          padding: 0 8px;
          cursor: pointer;
          position: relative;
          z-index: 4;
          font-family: var(--wc-font-medium);
          font-size: 13px;
          line-height: 18px;
          white-space: nowrap;
          color: var(--wc-text-2);
          svg{
            width: 16px;
            height: 16px;
            min-width: 16px;
          }
          svg path{
            transition: 0.2s;
          }
          &.active,
          &:hover{
            color: var(--wc-text);
            svg path{
              fill: var(--wc-text);
            }
          }
        }
      }
      &__back{
        padding-right: 15px;
        display: flex;
        align-items: center;
      }
      &__close{
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        cursor: pointer;
        position: absolute;
        right: 20px;
        top: 20px;
        z-index: 10;
      }
      // Profile block, centered (design)
      &__info{
        flex: none;
        padding: 20px 16px 16px;
        display: flex;
        justify-content: center;
        background-color: var(--wc-surface);
        border-bottom: 1px solid var(--wc-border);
        @media only screen and (max-width: 767px) {
          //padding: 10px 15px;
        }
        &-left{
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          min-width: 0;
          width: 100%;
          text-align: center;
        }
        &-right{
          display: flex;
          align-items: center;
          gap: 15px;
        }
        &-userInfo{
          cursor: pointer;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          svg{
            path{
              transition: 0.3s;
            }
          }
          &:hover{
            svg{
              path{
                fill: var(--wc-text);
              }
            }
          }
          &.active{
            svg{
              path{
                transition: 0.3s;
                fill: var(--wc-text);
              }
            }
          }
        }
        &-tooltip{
          cursor: pointer;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          &.no-touch{
            pointer-events: none;
          }
          .tooltip-body{
            opacity: 0;
            border-radius: 4px;
            transition: 0.3s;
            pointer-events: none;
            position: absolute;
            min-width: 300px;
            top: 40px;
            right: -10px;
            z-index: 15;
            box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.25);
            background: var(--wc-surface-2);
            &::before{
              content: "";
              position: absolute;
              right: 16px;
              top: -6px;
              width: 12px;
              height: 12px;
              background: var(--wc-surface-2);
              -webkit-transform: rotateZ(45deg);
              transform: rotateZ(45deg);
              z-index: -1;
            }
            &::after{
              content: '';
              position: absolute;
              top: -40px;
              height: 40px;
              width: 100%;
              right: 0;
              @media only screen and (max-width: 1023px) {
                content: none;
              }
            }
            &__row{
              display: flex;
              align-items: center;
              gap: 15px;
              padding: 12px 20px;
              transition: 0.3s;
              &:first-child{
                border-radius: 4px 4px 0 0;
              }
              &:last-child{
                border-radius: 0 0 4px 4px;
              }
              &:hover{
                background: var(--wc-surface);
                svg{
                  path{
                    fill: var(--wc-text);
                  }
                }
                p{
                  color: var(--wc-text);
                }
              }
              svg{
                path{
                  transition: 0.3s;
                }
              }
              p{
                transition: 0.3s;
                font-family: "Roboto-Regular", sans-serif;
                font-weight: 400;
                font-size: 16px;
                white-space: nowrap;
                line-height: 1.5;
                color: var(--wc-text-2);
              }
            }
          }
          &:hover{
            .tooltip-body{
              opacity: 1;
              pointer-events: all;
            }
          }
          
        }
        &-img{
          width: 64px;
          height: 64px;
          min-width: 64px;
          min-height: 64px;
          position: relative;
          margin: 0 0 8px;
          // @media only screen and (max-width: 1023px) {
          //   width: 40px;
          //   height: 40px;
          //   min-width: 40px;
          //   min-height: 40px;
          // }
          img{
            width: 100%;
            height: 100%;
            border-radius: 100%;
            overflow: hidden;
            object-fit: cover;
          }
          .online-status{
            position: absolute;
            right: 2px;
            bottom: 2px;
            width: 14px;
            height: 14px;
            border-radius: 100%;
            @media only screen and (max-width: 1023px) {
              width: 11px;
              height: 11px;
            }
            .ico-user-status{
              display: block;
              width: 100%;
              height: 100%;
              border-radius: 50%;
              box-sizing: border-box;
              border: 2px solid var(--wc-surface);
              background: var(--wc-online);
            }
          }
        }
        &-name{
          min-width: 0;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          .user-name{
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;
            max-width: 100%;
            &.name-unmute{
              -webkit-mask-image: linear-gradient(90deg, black 90%, transparent);
            }
            a{
              font-family: var(--wc-font-medium);
              font-weight: 400;
              font-size: 15px;
              line-height: 20px;
              white-space: nowrap;
              text-overflow: ellipsis;
              text-decoration: none;
              color: var(--wc-text);
              min-width: 0;
              overflow: hidden;
              // @media only screen and (max-width: 1023px) {
              //   font-size: 16px;
              // }
              &.fade-name{
                width: 100%;
                overflow: hidden;
                -webkit-mask-image: linear-gradient(90deg, black 90%, transparent);
              }
            }
            // @media only screen and (max-width: 1023px) {
            //   font-size: 16px;
            //   margin-bottom: 0;
            //   padding-top: 0;
            // }
          }
          .user-region{
            margin: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            &__flag{
              width: 16px;
              height: 16px;
              min-width: 16px;
              display: flex;
              justify-content: center;
              border-radius: 100%;
              overflow: hidden;
              img{
                width: auto;
                max-width: none;
                height: 100%;
              }
            }
            span{
              font-family: var(--wc-font);
              font-weight: 400;
              font-size: 13px;
              line-height: 18px;
              color: var(--wc-text-2);
            }
          }
        }
      }
      &__content{
        height: 100%;
        min-height: 0;
        overflow-y: auto;
        .content-block{
          border-radius: 0;
          overflow: hidden;
          padding: 4px 16px 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          .order{
            display: flex;
            gap: 10px;
            border-radius: 12px;
            padding: 5px 10px 5px 5px;
            cursor: pointer;
            position: relative;
            background-color: var(--wc-bg);
            border: 1px solid var(--wc-border);
            transition: 0.4s;
            &:hover{
              border: 1px solid var(--wc-border);
              background-color: var(--wc-surface);
            }
            &.active{
              border: 1px solid #42C01880
            }
            &__img{
              width: 140px;
              min-width: 140px;
              height: 70px;
              border-radius: 12px;
              overflow: hidden;
              position: relative;
                &.many{
                  position: relative;
                  img{
                      position: absolute;
                      width: 100%;
                      height: 100%;
                      left: 0;
                      top: 0;
                      border-radius: 12px;
                  }
                  .first{
                      left: 8px;
                      z-index: 2;
                  }
                  .second{
                      z-index: 1;
                  }
              }
            }
            &__block{
              display: flex;
              flex-direction: column;
              width: 100%;
              min-width: 0;
              &-name{
                padding-bottom: 2px;
              }
              &-price,
              &-name{
                display: flex;
                align-items: center;
                gap: 5px;
                &-counter{
                  background-color: var(--wc-own);
                  border-radius: 50px;
                  padding: 2px 5px;
                  color: var(--wc-text-3);
                  font-family: "Roboto-Regular";
                  font-weight: 400;
                  font-size: 10px;
                  line-height: 120%;
                  letter-spacing: 0px;
                }
                h5{
                  font-family: 'Roboto-Medium';
                  font-weight: 400;
                  font-size: 14px;
                  line-height: 140%;
                  color: var(--wc-text);
                  // overflow: hidden;
                  white-space: nowrap;
                  margin: 0;
                }
              }
              &-animate{
                overflow: hidden;
                &.long-name{
                  padding-right: 10px;
                  -webkit-mask-image: linear-gradient(90deg, black 90%, transparent);
                  h5{
                    position: relative;
                    padding-left: 10px;
                    &:after {
                      content: attr(data-text);
                      position: absolute;
                      left: 100%;
                      top: 0;
                      white-space: nowrap;
                      pointer-events: none;
                    }
                  }
                  @keyframes textAnimationName {
                      0% {
                          transform: translateX(0%);
                      }

                      20% {
                          transform: translateX(0%);
                      }

                      100% {
                          transform: translateX(-100%);
                      }
                  }
                  &:hover{
                      -webkit-mask-image: linear-gradient(
                          90deg,
                          transparent 0%,
                          black 10%,
                          black 90%,
                          transparent 100%
                          );
                        h5{
                            animation: textAnimationName 10s linear infinite;
                        }
                      
                  }
                }
              }
              &-id{
                font-family: "Roboto-Regular";
                font-weight: 400;
                font-size: 12px;
                line-height: 120%;
                color: var(--wc-text-3);
                padding-bottom: 10px;
              }
            }
            &__status{
              position: absolute;
              bottom: 10px;
              right: 10px;
              background: var(--wc-accent-soft);
              border-radius: 7px;
              padding: 3px 7px;
              display: flex;
              align-items: center;
              gap: 5px;
              span{
                font-family: "Roboto-Regular";
                font-weight: 400;
                font-size: 12px;
                line-height: 120%;
                letter-spacing: 0px;
                color: var(--wc-accent);
                display: flex;
                align-items: center;
                text-transform: capitalize;
              }
              &.close-dispute,
              &.delivered{
                background: var(--wc-success-soft);
                span{
                    color: var(--wc-success);
                }
                :deep(.wrap){
                    width: 14px;
                    height: 14px;
                    min-width: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: var(--wc-success);
                    border-radius: 50%;
                    padding-left: 1px;
                    padding-top: 1px;
                }
              }
              &.close-dispute-red{
                background: var(--wc-danger-soft);
                :deep(.wrap){
                    width: 14px;
                    height: 14px;
                    min-width: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: var(--wc-danger-soft);
                    border-radius: 50%;
                }
                span{
                    color: var(--wc-danger);
                }
              }
            }
          }
        }
        &.mini{
          .content-block{
            padding: 20px 15px;
          }
        }
      }
    }
    .feedback{
      border-radius: 16px;
      padding: 10px 12px;
      background-color: var(--wc-bg);
      position: relative;
      overflow: hidden;
      &__header{
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-bottom: 10px;
        &-left{
          display: flex;
          align-items: center;
          gap: 10px;
        }
        &-user{
          display: flex;
          align-items: center;
          gap: 10px;
          img{
            max-width: 30px;
            max-height: 30px;
            border-radius: 100%;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
          }
          span{
            font-family: "Roboto-Regular", sans-serif;
            font-weight: 400;
            font-size: 16px;
            line-height: 24px;
            color: var(--wc-text);
            width: 100%;
            overflow: hidden;
            white-space: nowrap;
            @media only screen and (max-width: 1639px) {
              max-width: 100px;
             // -webkit-mask-image: linear-gradient(90deg, black 90%, transparent);
            }
          }
        }
        &-time{
          font-family: "Roboto-Regular", sans-serif;
          font-weight: 400;
          font-size: 14px;
          line-height: 1.4;
          color: var(--wc-text-3);
          padding-left: 13px;
          position: relative;
          &::before{
            content: '';              
            position: absolute;
            left: 0;
            top: 50%;
            border-radius: 100%;
            transform: translateY(-50%);
            width: 3px;
            height: 3px;
            background-color: var(--wc-text-4);
          }
        }
        &-right{
          display: flex;
          align-items: center;
          gap: 10px;
          svg{
            cursor: pointer;
            path{
              transition: 0.3s;
            }
            &:hover path{
              fill: var(--wc-text);
            }
          }
        }
      }
      &__body{
        font-family: "Roboto-Regular", sans-serif;
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        color: var(--wc-text-2);
        padding-right: 18px;
        max-height: 48px;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        transition: max-height 0.3s ease;
      }
      &__btn{
        position: absolute;
        right: 12px;
        bottom: 12px;
        display: flex;
        align-items: center;
        cursor: pointer;
        svg{
          transition: 0.4s;
          path{
            transition: 0.4s;
          }
          &:hover path{
            fill: var(--wc-text);
          }
        }
      }
      &.active{
        .feedback__body{
          max-height: 1000px;
          -webkit-line-clamp: unset;
        }
        .feedback__btn svg{
          transform: rotate(180deg);
          path{
            fill: var(--wc-text);
          }
        }
      }
    }
    .media-date-group{
      position: relative;
      padding-top: 12px;
      .media-date{
        padding: 0;
        position: static;
        cursor: default;
        margin-bottom: 8px;
        transition: opacity 0.3s;
        &__block{
            font-family: var(--wc-font-medium);
            font-weight: 400;
            font-size: 12px;
            line-height: 16px;
            text-align: left;
            color: var(--wc-text-3);
        }
        &.hidden {
            opacity: 0;
            pointer-events: none;
        }
      }
    }
    .media-grid{
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 4px;
      .media-img{
        width: 100%;
        border-radius: 6px;
        overflow: hidden;
        aspect-ratio: 1 / 1;
        max-width: none;
        background-color: var(--wc-surface-3);
        cursor: pointer;
        position: relative;
        background-size: cover;
        background-position: center;
        img {
          border-radius: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          transition: opacity 0.3s ease;
          display: block;
          position: relative;
          z-index: 2;
        }
        &::after{
          border-radius: 4px;
          content: "";
          z-index: 1;
          position: absolute;
          inset: 0;
          background: transparent;
          //backdrop-filter: blur(4px);
        }
      }
    }
    .files-list{
      display: flex;
      flex-direction: column;
      gap: 5px;
      .file{
        height: 48px;
        border-radius: 8px;
        background-color: var(--wc-surface);
        border: 1px solid var(--wc-border);
        padding: 0 4px 0 6px;
        display: flex;
        align-items: center;
        gap: 10px;
        transition: 0.2s;
        &:hover{
          background-color: var(--wc-bg);
          .file__btn{
            opacity: 1;
          }
        }
    .file__preview-modal{
        width: 36px;
        height: 36px;
        min-width: 36px;
        padding: 0;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        background-image: url("/img/file-ext/pdf.svg");
        background-size: 36px;
        background-repeat: no-repeat;
        background-position: center center;
        &.pdf {
            background-image: url("/img/file-ext/pdf.svg");
        }
        &.csv {
            background-image: url("/img/file-ext/csv.svg");
        }
        &.gif {
            background-image: url("/img/file-ext/gif.svg");
        }
        &.rar {
            background-image: url("/img/file-ext/rar.svg");
        }
        &.txt {
            background-image: url("/img/file-ext/txt.svg");
        }
        &.xls {
            background-image: url("/img/file-ext/xls.svg");
        }
        &.xlsx {
            background-image: url("/img/file-ext/xlsx.svg");
        }
        &.zip {
            background-image: url("/img/file-ext/zip.svg");
        }
        }
        span{
          width: 100%;
          min-width: 0;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          font-family: var(--wc-font-medium);
          font-weight: 400;
          font-size: 13px;
          line-height: 18px;
          color: var(--wc-text);
        }
        .file__btn{
          display: flex;
          align-items: center;
          gap: 0;
          span{
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border-radius: 6px;
            svg{
            width: 16px;
            height: 16px;
            color: var(--wc-text-2);
            transition: 0.4s;
            path{
              transition: 0.4s;
            }
            }
            &:hover svg{
              color: var(--wc-text);
              path{
                  stroke: var(--wc-text);
              }
            }
          }
        }
        &:hover{
          @media only screen and (min-width: 1024px) {
            background-color: var(--wc-surface-2);
            .file__btn{
                opacity: 1;
            }
          }
        }
      }
    }
    .user-name-tooltip {
      position: absolute;
      top: 100%;
      left: 0;
      background-color: var(--wc-surface-2);
      color: var(--wc-text);
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 14px;
      white-space: nowrap;
      z-index: 10;
      box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.25);
      &::before {
        content: "";
        position: absolute;
        bottom: 100%;
        left: 10px;
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 6px solid var(--wc-border);
      }
    }
</style>